import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Box,
  Button,
  Divider,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Textarea,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';
import type { CordeCommonModelsDtoTeamTeam } from '@/api/generated/v1/api';

interface TeamSettingsTabProps {
  team: CordeCommonModelsDtoTeamTeam;
  isActive?: boolean;
}

export const TeamSettingsTab: React.FC<TeamSettingsTabProps> = ({
  team,
  isActive = false,
}) => {
  const { t } = useTranslation();
  const { colorSchemes } = useThemeContext();
  const toast = useToast();
  const teamService = useTeamService();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.800', 'gray.100');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // Settings state
  const [securitySettings, setSecuritySettings] = useState<any>(null);
  const [uiSettings, setUISettings] = useState<any>(null);
  const [notificationSettings, setNotificationSettings] = useState<any>(null);
  const [userSettings, setUserSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Load all settings
  const loadSettings = async () => {
    console.log('🔄 Loading team settings for team:', team.id);
    try {
      setLoading(true);
      
      console.log('📡 Making API calls to load settings...');
      const [security, ui, notifications, users] = await Promise.all([
        teamService.getTeamSecuritySettings(team.id!).then(result => {
          console.log('🔒 Security settings loaded:', result);
          return result;
        }).catch(error => {
          console.warn('⚠️ Security settings failed:', error);
          return null;
        }),
        teamService.getTeamUISettings(team.id!).then(result => {
          console.log('🎨 UI settings loaded:', result);
          return result;
        }).catch(error => {
          console.warn('⚠️ UI settings failed:', error);
          return null;
        }),
        teamService.getTeamNotificationSettings(team.id!).then(result => {
          console.log('🔔 Notification settings loaded:', result);
          return result;
        }).catch(error => {
          console.warn('⚠️ Notification settings failed:', error);
          return null;
        }),
        teamService.getTeamUserSettings(team.id!).then(result => {
          console.log('👥 User settings loaded:', result);
          return result;
        }).catch(error => {
          console.warn('⚠️ User settings failed:', error);
          return null;
        }),
      ]);

      console.log('✅ All settings loaded:', { security, ui, notifications, users });
      
      setSecuritySettings(security);
      setUISettings(ui);
      setNotificationSettings(notifications);
      setUserSettings(users);
    } catch (error) {
      console.error('❌ Failed to load team settings:', error);
      toast({
        title: t('common.error'),
        description: t('teams.settings.loadError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 TeamSettingsTab useEffect triggered, team.id:', team.id);
    if (team.id) {
      loadSettings();
    }
  }, [team.id]); // Removed t and toast from dependencies to avoid unnecessary re-renders

  // Reload settings when tab becomes active
  useEffect(() => {
    console.log('🔄 TeamSettingsTab isActive changed:', isActive);
    if (isActive && team.id) {
      console.log('⚙️ Settings tab is now active, reloading settings...');
      loadSettings();
    }
  }, [isActive, team.id]);

  // Handle tab change - reload data when switching tabs
  const handleTabChange = (index: number) => {
    console.log('🔄 Tab changed from', activeTab, 'to', index);
    setActiveTab(index);
    // Reload settings when switching tabs to ensure fresh data
    if (team.id) {
      console.log('🔄 Reloading settings due to tab change...');
      loadSettings();
    }
  };

  const handleSaveSettings = async (type: string, settings: any) => {
    try {
      setSaving(true);
      let result;
      
      switch (type) {
        case 'security':
          result = await teamService.updateTeamSecuritySettings(team.id!, settings);
          setSecuritySettings(result);
          break;
        case 'ui':
          result = await teamService.updateTeamUISettings(team.id!, settings);
          setUISettings(result);
          break;
        case 'notifications':
          result = await teamService.updateTeamNotificationSettings(team.id!, settings);
          setNotificationSettings(result);
          break;
        case 'users':
          result = await teamService.updateTeamUserSettings(team.id!, settings);
          setUserSettings(result);
          break;
      }

      toast({
        title: t('common.success'),
        description: t('teams.settings.saveSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: t('common.error'),
        description: t('teams.settings.saveError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card bg="bg.surface" borderColor={borderColor}>
        <CardBody>
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" color={colorSchemes.primary} />
          </Flex>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg="bg.surface" borderColor={borderColor}>
      <CardHeader>
        <HStack justifyContent="space-between">
          <Heading size="md" color={headingColor}>
            {t('teams.settings')}
          </Heading>
          <Button
            size="sm"
            variant="outline"
            colorScheme={colorSchemes.primary}
            onClick={() => {
              console.log('🔄 Manual refresh triggered');
              loadSettings();
            }}
            isLoading={loading}
          >
            {t('common.refresh')}
          </Button>
        </HStack>
      </CardHeader>
      <CardBody>
        <Tabs 
          variant="enclosed" 
          index={activeTab} 
          onChange={handleTabChange}
          colorScheme={colorSchemes.primary}
        >
          <TabList>
            <Tab _selected={{ color: colorSchemes.primary, borderColor: colorSchemes.primary }}>
              {t('teams.settings.general')}
            </Tab>
            <Tab _selected={{ color: colorSchemes.primary, borderColor: colorSchemes.primary }}>
              {t('teams.settings.security')}
            </Tab>
            <Tab _selected={{ color: colorSchemes.primary, borderColor: colorSchemes.primary }}>
              {t('teams.settings.ui')}
            </Tab>
            <Tab _selected={{ color: colorSchemes.primary, borderColor: colorSchemes.primary }}>
              {t('teams.settings.notifications')}
            </Tab>
            <Tab _selected={{ color: colorSchemes.primary, borderColor: colorSchemes.primary }}>
              {t('teams.settings.users')}
            </Tab>
          </TabList>

          <TabPanels>
            {/* General Settings */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontWeight="semibold" mb={4} color={headingColor}>
                    {t('teams.teamInformation')}
                  </Text>
                  <VStack spacing={3} align="start">
                    <HStack>
                      <Text fontSize="sm" color={textColor} minW="100px">
                        <strong>{t('common.id')}:</strong>
                      </Text>
                      <Badge colorScheme={colorSchemes.primary}>{team.id}</Badge>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color={textColor} minW="100px">
                        <strong>{t('common.type')}:</strong>
                      </Text>
                      <Badge colorScheme={colorSchemes.secondary}>{team.team_type || 'project'}</Badge>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color={textColor} minW="100px">
                        <strong>{t('common.status')}:</strong>
                      </Text>
                      <Badge colorScheme={colorSchemes.secondary}>{team.status || 'active'}</Badge>
                    </HStack>
                    {team.created_at && (
                      <HStack>
                        <Text fontSize="sm" color={textColor} minW="100px">
                          <strong>{t('common.created')}:</strong>
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          {new Date(team.created_at).toLocaleDateString()}
                        </Text>
                      </HStack>
                    )}
                    {team.updated_at && (
                      <HStack>
                        <Text fontSize="sm" color={textColor} minW="100px">
                          <strong>{t('common.updated')}:</strong>
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          {new Date(team.updated_at).toLocaleDateString()}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="semibold" mb={4} color={headingColor}>
                    {t('teams.editTeam')}
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>{t('common.name')}</FormLabel>
                      <Input
                        value={team.name || ''}
                        onChange={(e) => {
                          // Update team name locally for immediate feedback
                          // This would need proper state management in a real app
                        }}
                        placeholder={t('teams.teamNamePlaceholder')}
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>{t('common.description')}</FormLabel>
                      <Textarea
                        value={team.description || ''}
                        onChange={(e) => {
                          // Update team description locally
                        }}
                        placeholder={t('teams.teamDescriptionPlaceholder')}
                        rows={3}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('teams.teamType')}</FormLabel>
                      <Select
                        value={team.team_type || 'project'}
                        onChange={(e) => {
                          // Update team type locally
                        }}
                      >
                        <option value="project">{t('teams.types.project')}</option>
                        <option value="department">{t('teams.types.department')}</option>
                        <option value="organization">{t('teams.types.organization')}</option>
                        <option value="community">{t('teams.types.community')}</option>
                      </Select>
                    </FormControl>

                    <Button
                      colorScheme={colorSchemes.primary}
                      onClick={() => {
                        // Handle team update
                        toast({
                          title: t('common.info'),
                          description: 'Team editing functionality will be implemented',
                          status: 'info',
                          duration: 3000,
                          isClosable: true,
                        });
                      }}
                      isLoading={saving}
                    >
                      {t('teams.updateTeam')}
                    </Button>
                  </VStack>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="semibold" mb={2} color="red.500">
                    {t('common.dangerZone')}
                  </Text>
                  <VStack spacing={2} align="start">
                    <Button variant="outline" size="sm" colorScheme={colorSchemes.primary}>
                      {t('teams.transferOwnership')}
                    </Button>
                    <Button variant="outline" size="sm" colorScheme={colorSchemes.primary}>
                      {t('teams.leaveTeam')}
                    </Button>
                    <Button colorScheme={colorSchemes.error} size="sm">
                      {t('teams.deleteTeam')}
                    </Button>
                  </VStack>
                </Box>
              </VStack>
            </TabPanel>

            {/* Security Settings */}
            <TabPanel>
              {securitySettings ? (
                <VStack spacing={6} align="stretch">
                  <HStack justifyContent="space-between">
                    <Text fontWeight="semibold" color={headingColor}>
                      {t('teams.settings.security')}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme={colorSchemes.primary}
                      onClick={() => {
                        console.log('🔄 Refreshing security settings...');
                        loadSettings();
                      }}
                      isLoading={loading}
                    >
                      {t('common.refresh')}
                    </Button>
                  </HStack>
                  
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="require-invitation" mb="0">
                      {t('teams.settings.requireInvitation')}
                    </FormLabel>
                    <Switch
                      id="require-invitation"
                      colorScheme={colorSchemes.primary}
                      isChecked={securitySettings.require_invitation}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        require_invitation: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="allow-self-join" mb="0">
                      {t('teams.settings.allowSelfJoin')}
                    </FormLabel>
                    <Switch
                      id="allow-self-join"
                      colorScheme={colorSchemes.primary}
                      isChecked={securitySettings.allow_self_join}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        allow_self_join: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t('teams.settings.maxMembers')}</FormLabel>
                    <NumberInput
                      value={securitySettings.max_members || 0}
                      onChange={(value) => setSecuritySettings({
                        ...securitySettings,
                        max_members: parseInt(value) || 0
                      })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t('teams.settings.passwordMinLength')}</FormLabel>
                    <NumberInput
                      value={securitySettings.password_min_length || 8}
                      min={6}
                      max={32}
                      onChange={(value) => setSecuritySettings({
                        ...securitySettings,
                        password_min_length: parseInt(value) || 8
                      })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <Button
                    colorScheme={colorSchemes.primary}
                    onClick={() => handleSaveSettings('security', securitySettings)}
                    isLoading={saving}
                  >
                    {t('common.save')}
                  </Button>
                </VStack>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  {t('teams.settings.notAvailable')}
                </Alert>
              )}
            </TabPanel>

            {/* UI Settings */}
            <TabPanel>
              {uiSettings ? (
                <VStack spacing={6} align="stretch">
                  <Text fontWeight="semibold" color={headingColor}>
                    {t('teams.settings.ui')}
                  </Text>
                  
                  <FormControl>
                    <FormLabel>{t('teams.settings.primaryColor')}</FormLabel>
                    <Input
                      type="color"
                      value={uiSettings.primary_color || '#0066CC'}
                      onChange={(e) => setUISettings({
                        ...uiSettings,
                        primary_color: e.target.value
                      })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t('teams.settings.theme')}</FormLabel>
                    <Select
                      value={uiSettings.sidebar_style || 'modern'}
                      onChange={(e) => setUISettings({
                        ...uiSettings,
                        sidebar_style: e.target.value
                      })}
                    >
                      <option value="modern">Modern</option>
                      <option value="classic">Classic</option>
                      <option value="minimal">Minimal</option>
                    </Select>
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="dark-mode" mb="0">
                      {t('teams.settings.enableDarkMode')}
                    </FormLabel>
                    <Switch
                      id="dark-mode"
                      colorScheme={colorSchemes.primary}
                      isChecked={uiSettings.enable_dark_mode}
                      onChange={(e) => setUISettings({
                        ...uiSettings,
                        enable_dark_mode: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t('teams.settings.language')}</FormLabel>
                    <Select
                      value={uiSettings.default_language || 'en'}
                      onChange={(e) => setUISettings({
                        ...uiSettings,
                        default_language: e.target.value
                      })}
                    >
                      <option value="en">English</option>
                      <option value="ru">Русский</option>
                      <option value="es">Español</option>
                    </Select>
                  </FormControl>

                  <Button
                    colorScheme={colorSchemes.primary}
                    onClick={() => handleSaveSettings('ui', uiSettings)}
                    isLoading={saving}
                  >
                    {t('common.save')}
                  </Button>
                </VStack>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  {t('teams.settings.notAvailable')}
                </Alert>
              )}
            </TabPanel>

            {/* Notification Settings */}
            <TabPanel>
              {notificationSettings ? (
                <VStack spacing={6} align="stretch">
                  <Text fontWeight="semibold" color={headingColor}>
                    {t('teams.settings.notifications')}
                  </Text>
                  
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="email-notifications" mb="0">
                      {t('teams.settings.enableEmailNotifications')}
                    </FormLabel>
                    <Switch
                      id="email-notifications"
                      colorScheme={colorSchemes.primary}
                      isChecked={notificationSettings.enable_email_notifications}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        enable_email_notifications: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="push-notifications" mb="0">
                      {t('teams.settings.enablePushNotifications')}
                    </FormLabel>
                    <Switch
                      id="push-notifications"
                      colorScheme={colorSchemes.primary}
                      isChecked={notificationSettings.enable_push_notifications}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        enable_push_notifications: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="slack-notifications" mb="0">
                      {t('teams.settings.enableSlackNotifications')}
                    </FormLabel>
                    <Switch
                      id="slack-notifications"
                      colorScheme={colorSchemes.primary}
                      isChecked={notificationSettings.enable_slack_notifications}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        enable_slack_notifications: e.target.checked
                      })}
                    />
                  </FormControl>

                  <Button
                    colorScheme={colorSchemes.primary}
                    onClick={() => handleSaveSettings('notifications', notificationSettings)}
                    isLoading={saving}
                  >
                    {t('common.save')}
                  </Button>
                </VStack>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  {t('teams.settings.notAvailable')}
                </Alert>
              )}
            </TabPanel>

            {/* User Settings */}
            <TabPanel>
              {userSettings ? (
                <VStack spacing={6} align="stretch">
                  <Text fontWeight="semibold" color={headingColor}>
                    {t('teams.settings.users')}
                  </Text>
                  
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="allow-role-requests" mb="0">
                      {t('teams.settings.allowUsersToRequestRoles')}
                    </FormLabel>
                    <Switch
                      id="allow-role-requests"
                      colorScheme={colorSchemes.primary}
                      isChecked={userSettings.allow_users_to_request_roles}
                      onChange={(e) => setUserSettings({
                        ...userSettings,
                        allow_users_to_request_roles: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="auto-assign-role" mb="0">
                      {t('teams.settings.autoAssignDefaultRole')}
                    </FormLabel>
                    <Switch
                      id="auto-assign-role"
                      colorScheme={colorSchemes.primary}
                      isChecked={userSettings.auto_assign_default_role}
                      onChange={(e) => setUserSettings({
                        ...userSettings,
                        auto_assign_default_role: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="enable-invitations" mb="0">
                      {t('teams.settings.enableUserInvitations')}
                    </FormLabel>
                    <Switch
                      id="enable-invitations"
                      colorScheme={colorSchemes.primary}
                      isChecked={userSettings.enable_user_invitations}
                      onChange={(e) => setUserSettings({
                        ...userSettings,
                        enable_user_invitations: e.target.checked
                      })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t('teams.settings.invitationExpiryDays')}</FormLabel>
                    <NumberInput
                      value={userSettings.invitation_expiry_days || 7}
                      min={1}
                      max={30}
                      onChange={(value) => setUserSettings({
                        ...userSettings,
                        invitation_expiry_days: parseInt(value) || 7
                      })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <Button
                    colorScheme={colorSchemes.primary}
                    onClick={() => handleSaveSettings('users', userSettings)}
                    isLoading={saving}
                  >
                    {t('common.save')}
                  </Button>
                </VStack>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  {t('teams.settings.notAvailable')}
                </Alert>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </CardBody>
    </Card>
  );
}; 